/**
 * /cart 지면 되살리기 — 돌아왔을 때 장바구니가 서버와 다르면 다시 그린다.
 *
 * /cart 에서 상품 링크를 타고 PDP 로 갔다가 담고 뒤로가기로 돌아오면, 브라우저는 지면을 다시
 * 그리지 않고 얼려둔 DOM(bfcache)을 그대로 복원한다. 서버가 렌더한 그 시점의 장바구니가 남아
 * 새로 담은 상품이 보이지 않는다. 인앱은 탭마다 웹뷰가 독립적이라(docs/IN-APP.md) 다른 탭에서
 * 담고 장바구니 탭으로 돌아오는 경로에서도 같은 일이 생긴다.
 *
 * 그래서 지면이 다시 보일 때마다 서버 장바구니와 대조하고, **어긋날 때만** 섹션을 다시 렌더한다.
 * 무조건 다시 그리지 않는 이유는 cart-shipment-timer 같은 지면 상태가 매번 초기화되기 때문이다.
 *
 * 이 파일은 "지면이 낡았는가"만 판단한다. 앱 탭 배지는 app-cart-badge.js 가 같은 신호를 따로 듣는다.
 */

import { sectionRenderer } from '@theme/section-renderer';
import { logError } from '@theme/utilities';

/** 조회·렌더가 겹쳐 도는 것을 막는다. */
let inFlight = false;

/**
 * 장바구니 지면 루트. morph 로 갈릴 수 있어 매번 다시 찾는다.
 * @returns {HTMLElement | null}
 */
function cartItems() {
  return /** @type {HTMLElement | null} */ (document.querySelector('.main-cart cart-items-component'));
}

/** @returns {string} */
function cartJsUrl() {
  const base = window.Theme?.routes?.cart_url || '/cart';
  return `${base}.js`;
}

/**
 * 지면에 그려진 장바구니 지문.
 *
 * 수량은 item_count 에 이미 합산돼 따로 읽지 않는다. 줄 키 목록은 총량이 그대로인 교체
 * (A 빼고 B 담기)를 잡는다. 두 탭에서 서로 상쇄되는 수량 변경까지는 구분하지 못하지만,
 * 놓쳐도 다음 담기·수량 변경에서 서버 응답으로 다시 맞춰진다.
 *
 * @param {HTMLElement} root
 * @returns {string}
 */
function renderedSignature(root) {
  const count = root.querySelector('[ref="cartItemCount"]')?.textContent?.trim() || '0';
  const keys = Array.from(root.querySelectorAll('.cart-items__row'), (row) => row.dataset.key ?? '');

  return `${parseInt(count, 10) || 0}|${keys.join(',')}`;
}

/**
 * 서버 장바구니 지문. 렌더는 cart.items 를 그대로 한 줄씩 그리므로 순서까지 대응된다.
 * @param {{ item_count: number, items?: { key: string }[] }} cart
 * @returns {string}
 */
function serverSignature(cart) {
  const keys = (cart.items ?? []).map((item) => item.key);

  return `${cart.item_count}|${keys.join(',')}`;
}

/**
 * 서버 장바구니와 대조해 어긋날 때만 섹션을 다시 렌더한다.
 */
async function refreshIfStale() {
  const root = cartItems();
  if (!root || inFlight) return;

  // 수량 변경 요청이 떠 있는 동안은 그 응답이 지면을 갱신한다. 끼어들면 서로 덮어쓴다
  if (root.classList.contains('cart-items-disabled')) return;

  const { sectionId } = root.dataset;
  if (!sectionId) return;

  inFlight = true;

  try {
    const response = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const cart = await response.json();
    if (typeof cart?.item_count !== 'number') return;
    if (serverSignature(cart) === renderedSignature(root)) return;

    await sectionRenderer.renderSection(sectionId, { cache: false });
  } catch (error) {
    // 실패해도 지면은 그대로 둔다. 다음 복귀 때 다시 맞춘다
    logError('main-cart-refresh:refreshIfStale', error);
  } finally {
    inFlight = false;
  }
}

/**
 * 뒤로/앞으로 이동으로 들어온 지면인지. bfcache 가 아니어도 HTTP 캐시로 되살아난 지면은 낡았다.
 * @returns {boolean}
 */
function isHistoryNavigation() {
  const [navigation] = performance.getEntriesByType('navigation');

  return /** @type {PerformanceNavigationTiming | undefined} */ (navigation)?.type === 'back_forward';
}

// 뒤로가기 복귀. 새로 들어온 지면은 서버가 방금 그렸으므로 확인하지 않는다
window.addEventListener('pageshow', (event) => {
  if (event.persisted || isHistoryNavigation()) refreshIfStale();
});

// 인앱 탭 전환 · 브라우저 탭 전환으로 지면이 다시 보일 때
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) refreshIfStale();
});
